<template>
    <v-dialog :value="value" max-width="860px" scrollable persistent @input="$emit('input', $event)">
        <v-card>
            <v-card-title class="custom-modal-title text-uppercase black--text font-weight-bold">
                Trocar status em lote
                <v-spacer></v-spacer>
                <v-btn icon :disabled="applying" @click="close">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-text>
                <p class="body-2 grey--text mb-4">
                    Filtre as OS pelo período, empresa e status atual, confira a prévia e escolha o novo status.
                </p>

                <v-form ref="form">
                    <v-row dense>
                        <v-col cols="12" sm="6">
                            <v-text-field dense outlined type="date" label="Data inicial" v-model="filters.start_date"
                                hide-details="auto" :rules="[v => !!v || 'Informe a data inicial']"
                                @input="resetPreview"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-text-field dense outlined type="date" label="Data final" v-model="filters.end_date"
                                hide-details="auto" :rules="[v => !!v || 'Informe a data final',
                                    v => !filters.start_date || !v || v >= filters.start_date || 'Anterior à data inicial']"
                                @input="resetPreview"></v-text-field>
                        </v-col>
                        <v-col cols="12">
                            <v-autocomplete dense outlined multiple chips small-chips deletable-chips clearable
                                label="Empresas (vazio = todas)" :items="customerItems" item-value="id" item-text="name"
                                :loading="customerLoading" :search-input.sync="customerSearch" :filter="() => true"
                                v-model="filters.customer_ids" hide-details="auto"
                                no-data-text="Nenhuma empresa encontrada" @change="onCustomersChange"></v-autocomplete>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select dense outlined multiple chips small-chips deletable-chips clearable
                                label="Status atual (vazio = todos)" :items="statusTypes" item-value="id"
                                item-text="name" v-model="filters.current_status" hide-details="auto"
                                @change="resetPreview"></v-select>
                        </v-col>
                        <v-col cols="12" sm="6">
                            <v-select dense outlined label="Novo status" :items="statusTypes" item-value="id"
                                item-text="name" v-model="filters.new_status" hide-details="auto"
                                :rules="[v => !!v || 'Informe o novo status']" @change="resetPreview"></v-select>
                        </v-col>
                    </v-row>
                </v-form>

                <div class="mt-4">
                    <v-btn color="primary" outlined :loading="loadingPreview" :disabled="applying" @click="loadPreview">
                        <v-icon left small>mdi-text-search</v-icon>
                        Gerar prévia
                    </v-btn>
                </div>

                <!-- PREVIA -->
                <div v-if="preview" class="bulk-preview mt-5">
                    <v-alert v-if="!preview.total" dense text type="info" class="body-2 mb-0">
                        Nenhuma OS encontrada com esses filtros.
                    </v-alert>

                    <template v-else>
                        <div class="bulk-preview__summary">
                            <div>
                                <span class="bulk-preview__value">{{ preview.total }}</span>
                                <span class="bulk-preview__label">OS encontradas</span>
                            </div>
                            <div>
                                <span class="bulk-preview__value primary--text">{{ preview.to_change }}</span>
                                <span class="bulk-preview__label">serão alteradas para {{ newStatusName }}</span>
                            </div>
                            <div v-if="preview.unchanged">
                                <span class="bulk-preview__value grey--text">{{ preview.unchanged }}</span>
                                <span class="bulk-preview__label">já estão {{ newStatusName }}</span>
                            </div>
                        </div>

                        <div class="mb-3">
                            <v-chip v-for="row in preview.by_status" :key="`st-${row.status}`" small
                                :color="statusColor(row.status)" dark class="mr-2 mb-1">
                                {{ row.name }}: {{ row.total }}
                            </v-chip>
                        </div>

                        <v-alert v-if="preview.to_change > preview.limit" dense text type="error" class="body-2">
                            O limite por operação é de {{ preview.limit }} OS. Reduza o período ou refine os filtros.
                        </v-alert>

                        <v-simple-table v-if="preview.sample.length" dense class="bulk-preview__table">
                            <thead>
                                <tr>
                                    <th>OS</th>
                                    <th>Data/hora</th>
                                    <th>Itinerário</th>
                                    <th>Status atual</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="trip in preview.sample" :key="`trip-${trip.id}`">
                                    <td>#{{ trip.id }}</td>
                                    <td>{{ formatDate(trip.date) }} {{ trip.time || '' }}</td>
                                    <td class="text-truncate" style="max-width: 320px">{{ trip.itinerary || '-' }}</td>
                                    <td>
                                        <v-chip x-small dark :color="statusColor(trip.status)">{{ trip.status_name }}</v-chip>
                                    </td>
                                </tr>
                            </tbody>
                        </v-simple-table>
                        <p v-if="preview.to_change > preview.sample.length" class="text-caption grey--text mt-2 mb-0">
                            Mostrando {{ preview.sample.length }} de {{ preview.to_change }} OS.
                        </p>
                    </template>
                </div>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn text :disabled="applying" @click="close">Cancelar</v-btn>
                <v-btn color="primary" elevation="0" :loading="applying" :disabled="!canApply" @click="apply">
                    {{ preview && preview.to_change ? `Alterar ${preview.to_change} OS` : 'Alterar status' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import debounce from 'lodash/debounce'

import statusTypes from '@/common/data/service-status-types.data'

const STATUS_COLORS = { 1: 'grey', 2: 'red', 3: 'green', 4: 'blue', 5: 'purple' }

const emptyFilters = () => ({
    start_date: null,
    end_date: null,
    customer_ids: [],
    current_status: [],
    new_status: null
})

// Troca de status de OS em lote (filtros: período, empresas e status atual).
// A prévia mostra quantas OS serão alteradas; a aplicação confere se a
// quantidade continua a mesma antes de gravar.
export default {
    name: 'TripBulkStatusDialog',
    props: {
        value: { type: Boolean, default: false }
    },
    data: () => ({
        statusTypes,
        filters: emptyFilters(),
        preview: null,
        loadingPreview: false,
        applying: false,
        customers: [],
        selectedCustomers: [],
        customerSearch: null,
        customerLoading: false
    }),
    computed: {
        newStatusName() {
            const found = statusTypes.find(item => item.id === this.filters.new_status)
            return found ? found.name : '-'
        },
        canApply() {
            return !!this.preview && !!this.filters.new_status && this.preview.to_change > 0
                && this.preview.to_change <= this.preview.limit && !this.loadingPreview
        },
        // Mantém as empresas já escolhidas visíveis enquanto se busca outra
        customerItems() {
            const list = [...this.customers]
            this.selectedCustomers.forEach(customer => {
                if (!list.some(item => item.id === customer.id)) list.push(customer)
            })
            return list
        }
    },
    watch: {
        value(open) {
            if (!open) return

            this.filters = emptyFilters()
            this.preview = null
            this.selectedCustomers = []
            this.searchCustomers('')
            if (this.$refs.form) this.$refs.form.resetValidation()
        },
        customerSearch(value) {
            this.searchCustomers(value || '')
        }
    },
    methods: {
        searchCustomers: debounce(async function (filter) {
            this.customerLoading = true

            try {
                const result = await axios.get('api/v2/customers', { params: { page: 1, limit: 20, search: { name: filter } } })
                const content = result.data.content
                this.customers = content.data || content || []
            } catch (error) {
                console.error('Erro ao buscar empresas:', error)
            } finally {
                this.customerLoading = false
            }
        }, 400),

        onCustomersChange(ids) {
            this.selectedCustomers = this.customerItems.filter(item => ids.includes(item.id))
            this.customerSearch = null
            this.resetPreview()
        },

        resetPreview() {
            this.preview = null
        },

        payload() {
            return {
                start_date: this.filters.start_date,
                end_date: this.filters.end_date,
                customer_ids: this.filters.customer_ids,
                current_status: this.filters.current_status,
                new_status: this.filters.new_status
            }
        },

        async loadPreview() {
            if (!this.$refs.form.validate()) return

            this.loadingPreview = true

            try {
                const result = await axios.post('api/v2/trips/status/bulk/preview', this.payload())
                this.preview = result.data.content
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 })
            } finally {
                this.loadingPreview = false
            }
        },

        async apply() {
            if (!this.canApply) return

            const confirmed = await this.$dialog.confirm({
                title: 'Confirmar troca de status',
                text: `Alterar <strong>${this.preview.to_change} OS</strong> para <strong>${this.newStatusName}</strong>? Esta ação não pode ser desfeita em lote.`,
                actions: { false: 'Cancelar', true: 'Alterar' }
            })

            if (!confirmed) return

            this.applying = true

            try {
                const result = await axios.post('api/v2/trips/status/bulk', {
                    ...this.payload(),
                    expected_total: this.preview.to_change
                })

                const content = result.data.content

                this.$dialog.notify.success(`${content.updated} OS alterada(s) para ${content.new_status_name}`,
                    { position: 'top-right', timeout: 5000 })

                this.$emit('updated')
                this.$emit('input', false)
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 6000 })
            } finally {
                this.applying = false
            }
        },

        close() {
            this.$emit('input', false)
        },

        statusColor(status) {
            return STATUS_COLORS[status] || 'grey'
        },

        formatDate(value) {
            return value ? moment.utc(value).format('DD/MM/YYYY') : ''
        }
    }
}
</script>

<style lang="scss">
.bulk-preview__summary {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    padding: 12px 16px;
    border-radius: 8px;
    background: #f4f7fd;
    margin-bottom: 12px;

    > div {
        display: flex;
        flex-direction: column;
    }
}

.bulk-preview__value {
    font-size: 22px;
    font-weight: 700;
}

.bulk-preview__label {
    font-size: 12px;
    color: #5a6b85;
}

.bulk-preview__table {
    border: 1px solid #e5e9f0;
    border-radius: 8px;
    max-height: 320px;
    overflow-y: auto;
}
</style>
